import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

type Props = {
  onRetry?: () => void;
};

/**
 * AI 정리(organize) 실패 시 본문 영역에 표시하는 에러 배너 (Figma 26255:504942).
 * - 경고 배너(caution 8% 배경) + 안내 문구 + 다시 시도(새로고침) 버튼.
 * - 정리가 멈춘 상태이므로 진행 스켈레톤은 표시하지 않는다.
 * - AI 요약(commonSummary) 블록과는 분리된 organize 전용 에러 표시.
 */
const OrganizeError = ({ onRetry }: Props) => {
  const { t } = useTranslation();

  return (
    <Wrap>
      <Banner>
        <IconBox aria-hidden>
          <svg
            width="24"
            height="24"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 8.59986V5.60948M8 10.8164V10.8426M11.78 13.3332H4.22005C3.1874 13.3332 2.31595 12.6507 2.04177 11.717C1.92473 11.3184 2.06854 10.9032 2.28851 10.5498L6.06846 3.73385C6.95407 2.31072 9.04594 2.31072 9.93154 3.73386L13.7115 10.5498C13.9315 10.9032 14.0753 11.3184 13.9582 11.717C13.6841 12.6507 12.8126 13.3332 11.78 13.3332Z"
              stroke="#F55060"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconBox>
        <Message>{t('오류가 발생했습니다. 다시 시도해 주세요.')}</Message>
        <RetryButton
          type="button"
          onClick={onRetry}
          aria-label={t('AI 정리 다시 시도')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 4.5C13.0376 4.5 15.5 6.96243 15.5 10C15.5 13.0376 13.0376 15.5 10 15.5C6.96243 15.5 4.5 13.0376 4.5 10C4.5 8.27223 5.29669 6.73053 6.54274 5.72222"
              stroke="#F55060"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.5 5.4165H6.94444V7.86095"
              stroke="#F55060"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </RetryButton>
      </Banner>
    </Wrap>
  );
};

export default OrganizeError;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Figma: caution 8% 배경 배너 (bg #FEF1F2, radius 8px, padding 8/12, gap 8)
const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--dk-danger-soft, #fef1f2);
`;

const IconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

const Message = styled.p`
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 28px;
  color: var(--dk-danger, #f55060);
  word-break: break-word;
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  svg {
    display: block;
  }
`;
